/**
 * «Рассылки» → «По шаблонам»: воронка каждого шаблона WhatsApp по дню отправки —
 * отправлено → дошло → прочитано → ответили (из них записали и забронировали) → открыли кабинет →
 * записались → нажали на бота → запустили бота; итог — записаны (сделка номера вошла в «Ждем
 * клиента») и забронировано. Шаблоны собраны в группы по стадии, к которой привязана рассылка.
 * Слева — очередь рассылки сейчас, справа — когда её фон запускался и отправлял последний раз
 * (`GET /api/mailing/templates/queue`, раз в минуту). Клик по числу — эти получатели в «Чатах»
 * (`/chats?mailing=`), клик по строке шаблона — список ниже; время отправки — ссылка на чат. Событие
 * кабинета относится к последней выдаче ссылки до него (рассылка или ручная отправка из «Чатов»).
 * Спека `docs/specs/2026-10-03-mailing-templates-design.md`; числа — как у
 * `prokat-runner mailing-templates`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ExternalLink } from "lucide-react";
import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { LeadLink } from "@/components/LeadLink";
import { query } from "@/lib/api";
import { iso, plusDays } from "@/lib/dates";
import {
  loadMailingQueues,
  loadMailingTemplates,
  type TemplateCounts,
  type TemplateItem,
  type TemplateQueue,
  type TemplateRow,
  type TemplateStep,
} from "@/lib/mailingTemplates";

type Key = keyof TemplateCounts;
/**
 * `need` — каким шаблонам шаг доступен: any — всем, link — со ссылкой на кабинет, zapis — со
 * страницей записи, early — уходящим ДО записи (после «Ждем клиента» запись уже есть), prebook —
 * уходящим ДО брони. `wa` — шаг есть только у WhatsApp: у рассылки, ушедшей бесплатными каналами, — прочерк.
 */
type Col = { key: Key; head: string; hint: string; of?: Key; tone?: string; sep?: boolean; wa?: boolean; need: "any" | "link" | "zapis" | "early" | "prebook" };

const BLOCKS: { label: string; cols: Col[] }[] = [
  {
    label: "Сообщение",
    cols: [
      { key: "sent", head: "Отправлено", hint: "сообщений рассылки ушло за период; мелко — сколько из них шаблоном WhatsApp, остальное — бесплатным каналом (бот, площадка)", need: "any" },
      { key: "failed", head: "Не дошло", hint: "Wazzup вернул ошибку доставки; доля — от ушедших в WhatsApp. У строк MAX и Telegram — у номера нет MAX / Telegram (контакт не сохранился, клиенту не писали) или, мелко красным, сбой отправки: мессенджер есть, но вкладка не отправила", of: "sent_wa", tone: "text-err", wa: true, need: "any" },
      { key: "delivered", head: "Дошло", hint: "доставлено на телефон; доля — от ушедших в WhatsApp", of: "sent_wa", wa: true, need: "any" },
      { key: "read", head: "Прочитано", hint: "клиент открыл сообщение", of: "delivered", wa: true, need: "any" },
      { key: "replied", head: "Ответили", hint: "написали в WhatsApp после шаблона и до следующего шаблона", of: "delivered", tone: "text-ok", sep: true, wa: true, need: "any" },
    ],
  },
  {
    label: "Из ответивших",
    cols: [
      { key: "replied_recorded", head: "Записали", hint: "ответили на шаблон, и после отправки сделка номера вошла в «Ждем клиента»", of: "replied", tone: "text-ok", wa: true, need: "early" },
      { key: "replied_reserved", head: "Забронировали", hint: "ответили на шаблон, и после отправки договор сделки номера вошёл в «Бронь»", of: "replied", tone: "text-ok", sep: true, wa: true, need: "prebook" },
    ],
  },
  {
    label: "Кабинет my.uni-renter.ru",
    cols: [
      { key: "opened", head: "Открыли", hint: "открыли страницу из кнопки шаблона после отправки", of: "read", tone: "text-ok", need: "link" },
      { key: "booked", head: "Записались", hint: "сами выбрали или перенесли время на странице записи", of: "opened", tone: "text-ok", sep: true, need: "zapis" },
    ],
  },
  {
    label: "Нажали на бота",
    cols: [
      { key: "click_max", head: "MAX", hint: "нажали MAX на странице", of: "opened", need: "link" },
      { key: "click_telegram", head: "Telegram", hint: "нажали Telegram на странице", of: "opened", sep: true, need: "link" },
    ],
  },
  {
    label: "Запустили бота",
    cols: [
      { key: "bot_max", head: "MAX", hint: "запустили бота MAX с хэшем ссылки — подписались", of: "sent", tone: "text-ok", need: "link" },
      { key: "bot_telegram", head: "Telegram", hint: "запустили @unirenterbot с хэшем ссылки — подписались", of: "sent", tone: "text-ok", sep: true, need: "link" },
    ],
  },
  {
    label: "Итог по сделке",
    cols: [
      { key: "recorded", head: "Записаны", hint: "после отправки сделка номера вошла в «Ждем клиента» — записал сотрудник или клиент сам", of: "sent", tone: "text-ok", need: "early" },
      { key: "reserved", head: "Забронировано", hint: "после отправки договор сделки номера вошёл в «Бронь» или «Подписан»", of: "sent", tone: "text-ok", sep: true, need: "prebook" },
    ],
  },
];
const COLS = BLOCKS.flatMap((b) => b.cols);

const STEP: Record<TemplateStep, { label: string; tone: string }> = {
  failed: { label: "не дошло", tone: "text-err" },
  broken: { label: "сбой отправки", tone: "text-err" },
  delivered: { label: "дошло", tone: "text-fg-muted" },
  read: { label: "прочитано", tone: "text-fg-muted" },
  replied: { label: "ответил", tone: "text-ok" },
  opened: { label: "открыл кабинет", tone: "text-ok" },
  booked: { label: "записался сам", tone: "text-ok" },
  click_max: { label: "нажал MAX", tone: "text-fg" },
  click_telegram: { label: "нажал Telegram", tone: "text-fg" },
  click_whatsapp: { label: "нажал WhatsApp", tone: "text-fg" },
  bot_max: { label: "бот MAX", tone: "text-ok" },
  bot_telegram: { label: "бот Telegram", tone: "text-ok" },
  recorded: { label: "записан", tone: "text-ok" },
  reserved: { label: "забронировал", tone: "text-ok" },
};

function pct(a: number, b: number): string {
  return b ? `${Math.round((a * 100) / b)}%` : "";
}

/** «02.10 06:47» из `2026-10-02 06:47`. */
function dmt(at: string): string {
  return `${at.slice(8, 10)}.${at.slice(5, 7)} ${at.slice(11, 16)}`;
}

/** Название канала отправки в списке получателей. */
const CHANNEL: Record<string, string> = {
  whatsapp: "шаблон WhatsApp",
  whatsapp_window: "WhatsApp в окне 24 ч",
  telegram: "Telegram",
  max: "MAX",
  vk: "ВКонтакте",
  avito: "Авито",
  instagram: "Instagram",
  free: "бесплатный канал",
};
/** Строка списка: `вид` или `вид:канал` (строка канала внутри шаблона). */
const split = (k: string | null) => (k ?? "").split(":") as [string, string | undefined];

/** «Не дошло» у рассылки с нашего номера — у клиента нет мессенджера: контакт не сохранился. */
const NO_APP: Record<string, string> = {
  max_lead: "нет MAX",
  tg_lead: "нет Telegram",
  max_address: "нет MAX",
  tg_address: "нет Telegram",
};

/** Почему у шаблона нет шага; пусто — шаг есть. */
function missing(row: TemplateRow, { need, wa }: Col): string {
  if (wa && row.sent > 0 && row.sent_wa === 0) return "ушло бесплатными каналами, не WhatsApp — статусов доставки и ответа в WhatsApp нет";
  if (need === "early" && row.group === "visit") return "шаблон уходит после примерки";
  if (need === "early") return row.group === "wait" || row.group === "booked" ? "шаблон уходит уже записанным" : "";
  if (need === "prebook") return row.group === "booked" ? "шаблон уходит уже забронировавшим" : "";
  const ok = need === "any" || (need === "link" ? row.link !== null : row.link === "zapis");
  return ok ? "" : "у шаблона нет такой кнопки";
}

/** Числа строки; `row` — шаблон, `null` — итог. Число — ссылка: эти получатели в «Чатах». */
function Cells({ c, row, chats, sub }: { c: TemplateCounts; row: TemplateRow | null; chats: (step: Key) => string; sub?: boolean }) {
  return (
    <>
      {COLS.map((col) => {
        const sep = col.sep ? "border-r border-border" : "";
        const why = row ? missing(row, col) : "";
        if (why) {
          return (
            <td key={col.key} className={`px-3 py-1.5 text-fg-faint ${sep}`} title={why}>
              —
            </td>
          );
        }
        const v = c[col.key];
        return (
          <td key={col.key} className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${sep}`}>
            {v ? (
              <Link
                to={chats(col.key)}
                onClick={(e) => e.stopPropagation()}
                title="открыть этих получателей в «Чатах»"
                className={`text-[14px] font-semibold ${col.tone ?? "text-fg"} hover:underline`}
              >
                {v}
              </Link>
            ) : (
              <span className="text-[14px] font-semibold text-fg-faint">{v}</span>
            )}
            {col.of && v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, c[col.of])}</span>}
            {col.key === "failed" && row && NO_APP[row.key] && c.failed > c.broken && (
              <span className="ml-1.5 text-[12px] text-fg-muted" title={`у номера нет мессенджера — контакт не сохранился, клиенту не писали: ${c.failed - c.broken}`}>
                {NO_APP[row.key]} {c.failed - c.broken}
              </span>
            )}
            {col.key === "failed" && c.broken > 0 && (
              <Link
                to={chats("broken")}
                onClick={(e) => e.stopPropagation()}
                title={`из них сбой отправки — ${c.broken}: мессенджер у номера есть, но вкладка не отправила; остальные ${c.failed - c.broken} — у номера нет мессенджера`}
                className="ml-1.5 text-[12px] text-err hover:underline"
              >
                сбой {c.broken}
              </Link>
            )}
            {col.key === "sent" && !sub && c.sent_wa < c.sent && (
              <span className="ml-1.5 text-[12px] text-fg-muted" title={`из них шаблоном WhatsApp — ${c.sent_wa}, бесплатным каналом (бот, площадка) — ${c.sent - c.sent_wa}`}>
                WhatsApp {c.sent_wa}
              </span>
            )}
          </td>
        );
      })}
    </>
  );
}

/**
 * Очередь рассылки сейчас: задач стоит и что с ними будет — разбор теми же правилами, что у
 * отправки. `q` нет у шаблонов, которые шлём не мы (`own` — нет), и у своей рассылки, чей фон на
 * экран ещё не выведен; `loading` — очереди ещё не пришли.
 */
function QueueCell({ q, own, loading }: { q: TemplateQueue | undefined; own: boolean; loading: boolean }) {
  const td = "whitespace-nowrap border-r border-border px-3 py-1.5 tabular-nums";
  if (!q) {
    const why = own ? "очередь и фон этой рассылки на экран ещё не выведены" : "шлём не мы (Резерв или сотрудник) — очереди у нас нет";
    return (
      <td className={`${td} text-fg-faint`} title={loading ? "" : why}>
        {loading ? "…" : "—"}
      </td>
    );
  }
  const notes = Object.entries(q.notes).sort((a, b) => b[1] - a[1]);
  const stuck = q.wait > 0 && q.alarm !== "";
  const sub = q.ready ? `к отправке ${q.ready}` : q.ready === null ? "CRM недоступна" : (notes[0]?.[0] ?? "");
  const title = [
    q.wait ? `задач в очереди: ${q.wait}` : "очередь пуста",
    q.wait && q.ready !== null ? `ушло бы прямо сейчас: ${q.ready}` : "",
    ...notes.map(([k, v]) => `${v} — ${k}`),
    q.oldest ? `самая старая задача — от ${dmt(q.oldest)}` : "",
    stuck ? q.alarm : "",
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <td className={td} title={title}>
      <span className={`text-[14px] font-semibold ${q.wait ? (stuck ? "text-err" : "text-fg") : "text-fg-faint"}`}>{q.wait}</span>
      {q.wait > 0 && sub && <span className={`ml-1.5 text-[12px] ${stuck ? "text-err" : "text-fg-muted"}`}>{sub}</span>}
    </td>
  );
}

/** Фон рассылки: когда задание диспетчера запускалось и когда рассылка отправляла последний раз. */
function RunCells({ q, loading }: { q: TemplateQueue | undefined; loading: boolean }) {
  const td = "whitespace-nowrap px-3 py-1.5 tabular-nums";
  if (!q) {
    return (
      <>
        <td className={`${td} text-fg-faint`}>{loading ? "…" : "—"}</td>
        <td className={`${td} text-fg-faint`}>{loading ? "…" : "—"}</td>
      </>
    );
  }
  const title = [`задание диспетчера ${q.task}`, q.send_on ? "отправка клиентам включена" : "отправка клиентам выключена", q.run_error]
    .filter(Boolean)
    .join("\n");
  return (
    <>
      <td className={td} title={title}>
        <span className={q.alarm ? "font-medium text-err" : ""}>{q.run_at ? dmt(q.run_at) : "не запускался"}</span>
        {q.alarm ? (
          <span className="ml-1.5 text-[12px] text-err">{q.alarm}</span>
        ) : (
          !q.send_on && <span className="ml-1.5 text-[12px] text-fg-faint">отправка выключена</span>
        )}
      </td>
      <td className={`${td} ${q.last_sent ? "" : "text-fg-faint"}`} title="когда эта рассылка последний раз отправила шаблон клиенту, MSK">
        {q.last_sent ? dmt(q.last_sent) : "ещё не отправляла"}
      </td>
    </>
  );
}

function ItemRow({ x, title }: { x: TemplateItem; title: string }) {
  const td = "whitespace-nowrap px-3 py-1.5";
  // Ответ — своей колонкой; сбой отправки — вместо «не дошло», а не следом за ним.
  const steps = x.steps.filter((s) => s !== "replied" && !(s === "failed" && x.steps.includes("broken")));
  return (
    <tr className="border-t border-border hover:bg-bg-hover" data-annot={`получатель ${x.phone}`}>
      <td className={`${td} pl-4 tabular-nums`}>
        {x.chat_id ? (
          <Link to={`/chats/${x.chat_id}`} className="text-accent hover:underline" title="переписка в «Чатах»">
            {dmt(x.at)}
          </Link>
        ) : (
          dmt(x.at)
        )}
      </td>
      <td className={td}>
        {title}
        {x.channel !== "whatsapp" && (
          <span className="ml-1.5 text-fg-muted" title="ушло бесплатным каналом, не WhatsApp">
            {CHANNEL[x.channel] ?? x.channel}
          </span>
        )}
      </td>
      <td className={`${td} font-mono tabular-nums`}>
        <Link to={`/search?q=${x.phone}`} className="hover:underline" title="поиск по номеру">
          {x.phone}
        </Link>
      </td>
      <td className={td}>
        {x.lead_id && (
          <>
            <span className="mr-1 text-fg-faint">лид</span>
            <LeadLink id={x.lead_id} />
            {x.lead_status && <span className="ml-1.5 text-fg-muted">{x.lead_status}</span>}
          </>
        )}
        {x.deal_id && (
          <>
            <span className={`mr-1 text-fg-faint ${x.lead_id ? "ml-2" : ""}`}>сделка</span>
            <Link to={`/deals/${x.deal_id}`} className="font-mono underline" title="карточка сделки">
              {x.deal_id}
            </Link>
            {x.deal_stage && <span className="ml-1.5 text-fg-muted">{x.deal_stage}</span>}
          </>
        )}
        {!x.lead_id && !x.deal_id && <span className="text-fg-faint">—</span>}
      </td>
      <td className={td}>
        {x.steps.includes("failed") ? (
          <span className="text-fg-faint">—</span>
        ) : (x.channel === "max" || x.channel === "telegram") && x.steps.includes("replied") ? (
          <span className="text-ok" title={`клиент ответил в диалоге ${CHANNEL[x.channel]} с нашего номера`}>
            ответил в {CHANNEL[x.channel]}
          </span>
        ) : x.channel !== "whatsapp" ? (
          <span className="text-fg-faint" title="ответ считаем только в WhatsApp; переписка — по ссылке на времени отправки">
            —
          </span>
        ) : x.replied_at ? (
          <>
            <span className="text-ok">ответил {x.replied_at.slice(0, 10) === x.at.slice(0, 10) ? x.replied_at.slice(11, 16) : dmt(x.replied_at)}</span>
            {x.waiting && (
              <span className="ml-1.5 text-err" title="чат сейчас «Без ответа»: последнее сообщение — клиента, отметки «Ответили» нет">
                ждёт нашего ответа
              </span>
            )}
          </>
        ) : (
          <span className="text-fg-faint">не ответил</span>
        )}
      </td>
      <td className={td}>
        {steps.length === 0 && <span className="text-fg-faint">отправлено</span>}
        {steps.map((s, i) => (
          <span key={s}>
            {i > 0 && <span className="mx-1 text-fg-faint">→</span>}
            <span className={STEP[s].tone}>{s === "failed" ? (NO_APP[x.kind] ?? STEP[s].label) : STEP[s].label}</span>
          </span>
        ))}
      </td>
      <td className={td}>
        {x.url && (
          <a
            href={x.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex text-fg-faint hover:text-fg"
            title="страница, как её видит клиент (засчитается как открытие)"
          >
            <ExternalLink size={12} />
          </a>
        )}
      </td>
    </tr>
  );
}

export function TemplatesTab() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -6));
  const to = params.get("to") || iso(today);
  const kind = params.get("kind");
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["mailing-templates", from, to], queryFn: () => loadMailingTemplates(from, to) });
  const queues = useQuery({ queryKey: ["mailing-queue"], queryFn: loadMailingQueues, refetchInterval: 60_000 });
  const queue = queues.data?.kinds ?? {};
  const waiting = Object.values(queue).reduce((n, x) => n + x.wait, 0);
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const r = q.data;
  const [open, setOpen] = useState<Set<string>>(() => new Set(["fitting", "fitting_tried"]));
  const titles = Object.fromEntries((r?.rows ?? []).map((x) => [x.key, x.title]));
  const [kindKey, kindChannel] = split(kind);
  const items = (r?.items ?? []).filter((x) => !kind || (x.kind === kindKey && (!kindChannel || x.channel === kindChannel)));
  const titleOf = (k: string) => {
    const [a, b] = split(k);
    return (titles[a] ?? a) + (b ? ` · ${CHANNEL[b] ?? b}` : "");
  };
  const toggle = (k: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  /** «Чаты» с получателями шаблона (`all` — всех), дошедшими до шага: фильтр — в адресе. */
  const chats = (k: string, s: Key) => `/chats${query({ mailing: k, from, to, step: s === "sent" ? "" : s })}`;

  return (
    <section className="flex flex-col gap-3" data-annot="рассылки по шаблонам">
      <div className="flex flex-wrap items-center gap-3" data-annot="период по шаблонам">
        <DateRangePicker label="Отправлен" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю шаблоны…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(q.error.message)}</div>}
      {r && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="воронка по шаблонам">
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`} rowSpan={2}>
                    Шаблон
                  </th>
                  <th
                    className={`${th} border-r border-border align-bottom`}
                    rowSpan={2}
                    title="задач рассылки стоит в очереди сейчас (от периода не зависит); рядом — сколько ушло бы прямо сейчас или чего ждут: проверка теми же правилами, что у отправки"
                  >
                    В очереди
                  </th>
                  {BLOCKS.map((b) => (
                    <th key={b.label} className={`${th} border-r border-border font-normal`} colSpan={b.cols.length}>
                      {b.label}
                    </th>
                  ))}
                  <th className={`${th} font-normal`} colSpan={2}>
                    Фон рассылки
                  </th>
                </tr>
                <tr>
                  {COLS.map((c, i) => (
                    <th key={`${c.key}-${i}`} className={`${th} ${c.sep ? "border-r border-border" : ""}`} title={c.hint}>
                      {c.head}
                    </th>
                  ))}
                  <th className={th} title="когда фоновое задание рассылки запускалось последний раз, MSK; красным — фон стоит, упал или отправка выключена при непустой очереди">
                    Запускался
                  </th>
                  <th className={th} title="когда рассылка последний раз отправила шаблон клиенту, MSK">
                    Отправлял
                  </th>
                </tr>
              </thead>
              <tbody>
                {r.groups.map((g) => (
                  <Fragment key={g.key}>
                    <tr className="border-t border-border bg-bg" data-annot={`группа «${g.title}»`}>
                      <td colSpan={COLS.length + 4} className="px-4 pb-1 pt-2 text-[11.5px] font-medium text-fg-muted">
                        {g.title}
                      </td>
                    </tr>
                    {r.rows
                      .filter((x) => x.group === g.key)
                      .map((x) => {
                        const many = x.channels.length > 1;
                        const shown = many && open.has(x.key);
                        return (
                          <Fragment key={x.key}>
                            <tr
                              onClick={() => set({ kind: kind === x.key ? "" : x.key })}
                              title="клик по строке — получатели этого шаблона ниже"
                              className={`cursor-pointer border-t border-border hover:bg-bg-hover ${kind === x.key ? "bg-accent-dim" : ""}`}
                              data-annot={`шаблон «${x.title}»`}
                            >
                              <td className={`whitespace-nowrap py-1.5 pl-7 pr-3 ${x.sent ? "font-medium" : "text-fg-faint"}`}>
                                {many && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggle(x.key);
                                    }}
                                    className="-ml-5 mr-1 inline-grid size-4 place-items-center align-[-2px] text-fg-muted hover:text-fg"
                                    title={shown ? "свернуть каналы" : "по каналам: куда ушло"}
                                    aria-expanded={shown}
                                    data-annot="шаблон: по каналам"
                                  >
                                    <ChevronRight size={14} className={shown ? "rotate-90" : ""} />
                                  </button>
                                )}
                                <button type="button" className="hover:underline" aria-pressed={kind === x.key}>
                                  {x.title}
                                </button>
                              </td>
                              <QueueCell q={queue[x.key]} own={x.own} loading={!queues.data} />
                              <Cells c={x} row={x} chats={(s) => chats(x.key, s)} />
                              <RunCells q={queue[x.key]} loading={!queues.data} />
                            </tr>
                            {shown &&
                              x.channels.map((ch) => {
                                const k = `${x.key}:${ch.channel}`;
                                return (
                                  <tr
                                    key={k}
                                    onClick={() => set({ kind: kind === k ? "" : k })}
                                    title="клик по строке — получатели этого канала ниже"
                                    className={`cursor-pointer text-fg-muted hover:bg-bg-hover ${kind === k ? "bg-accent-dim" : ""}`}
                                    data-annot={`шаблон «${x.title}»: канал ${ch.channel}`}
                                  >
                                    <td className="whitespace-nowrap py-1 pl-12 pr-3 text-[12px]">{CHANNEL[ch.channel] ?? ch.channel}</td>
                                    <td className="border-r border-border" />
                                    {/* Строка канала судит по своим числам: у бота доставки WhatsApp нет — «—», не ноль. */}
                                    <Cells c={ch} row={{ ...x, ...ch }} chats={(s) => chats(k, s)} sub />
                                    <td colSpan={2} />
                                  </tr>
                                );
                              })}
                          </Fragment>
                        );
                      })}
                  </Fragment>
                ))}
                <tr className="border-t-2 border-border font-medium" data-annot="итого по шаблонам">
                  <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
                  <td className="border-r border-border px-3 py-1.5 tabular-nums" title="задач во всех очередях сейчас">
                    <span className={`text-[14px] font-semibold ${waiting ? "text-fg" : "text-fg-faint"}`}>
                      {queues.data ? waiting : "…"}
                    </span>
                  </td>
                  <Cells c={r.total} row={null} chats={(s) => chats("all", s)} />
                  <td colSpan={2} className="px-3 py-1.5 text-[11.5px] font-normal text-fg-faint">
                    {queues.isError ? <span className="text-err">очереди не получены</span> : queues.data && `на ${dmt(queues.data.at)}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            Период — день отправки шаблона. Число — ссылка: эти получатели в «Чатах» вместе с их чатами в других
            каналах. Получатель в каждом шаге считается один раз. Своя рассылка получает строку сама — новая появится
            здесь без настройки; «Прочие шаблоны» — только шаблоны WhatsApp, ушедшие мимо рассылок (пробы руками).
            Рассылка может уйти бесплатным каналом (бот Telegram или MAX, ВКонтакте, Авито, Instagram) — в «Отправлено»
            она есть, рядом мелко — сколько ушло в WhatsApp; доставка, прочтение и ответ считаются только по WhatsApp.
            Стрелка у шаблона — та же воронка по каналам, которыми ушло: шаблон WhatsApp, текст в WhatsApp в окне 24 ч
            (бесплатно), боты, площадки; число в строке канала — получатели этого канала. «Как прошла примерка» — двумя
            строками: «с сердечками» — у сделки есть платья в сетке примерки (кроме «не понравилось») или сообщение ушло
            с похожими нарядами, «без сердечек» — остальные.
            Доли: «Дошло» и «Не дошло» — от ушедших в WhatsApp, «Прочитано» и «Ответили» — от дошедших, «Из ответивших» — от ответивших, «Открыли» — от прочитавших,
            «Записались» и нажатия — от открывших, запуск бота, «Записаны» и «Забронировано» — от отправленных.
            «Ответили» — клиент написал в WhatsApp после шаблона и до следующего шаблона ему же. «Записаны» — после
            отправки сделка номера вошла в «Ждем клиента», кто бы ни записал; «Забронировано» — договор сделки номера
            вошёл в «Бронь» или «Подписан»; «Адрес салона» и «Договор проката» уходят уже записанным. «В очереди» и
            «Фон рассылки» — как сейчас, от периода не зависят и обновляются раз в минуту: задач стоит в очереди и
            сколько из них ушло бы прямо сейчас (остальные ждут окна салона или тишины по номеру — причины в
            подсказке), когда фон запускался и отправлял последний раз; красным — очередь стоит: фон не запускался,
            упал или отправка выключена. У «Лид 1 (Резерв)», «Дожима» и прочих очереди нет — шлём не мы. Действие в кабинете относится к
            последней выдаче ссылки до него: если ссылку позже отправил сотрудник из «Чатов», действие рассылке не
            засчитывается. «Договор проката» ведёт кнопками сразу в ботов; у «Лид 1 (Резерв)», «Дожима» и прочих
            ссылок на кабинет нет.
          </div>
        </section>
      )}
      {r && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="получатели шаблонов">
          <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-[12.5px]">
            <span className="font-medium">
              {kind ? titleOf(kind) : "Все шаблоны"}
            </span>
            <span className="tabular-nums text-fg-muted">{items.length} получателей</span>
            {kind && (
              <button type="button" onClick={() => set({ kind: "" })} className="text-accent hover:underline">
                показать всех
              </button>
            )}
            <Link to={chats(kind || "all", "sent")} className="text-accent hover:underline">
              открыть в «Чатах»
            </Link>
            {r.count > r.items.length && (
              <span className="text-fg-faint">в списке {r.items.length} свежих из {r.count} — сузьте период</span>
            )}
          </div>
          {items.length > 0 && (
            <div className="overflow-x-auto border-t border-border">
              <table className="w-auto text-[12.5px]">
                <thead className="text-left text-[11.5px] text-fg-faint">
                  <tr>
                    <th className={`${th} pl-4`} title="когда шаблон ушёл, MSK; клик — переписка в «Чатах»">Отправлен</th>
                    <th className={th}>Шаблон</th>
                    <th className={th}>Номер</th>
                    <th className={th} title="статус лида и стадия сделки — как сейчас в Bitrix">Лид / сделка</th>
                    <th className={th} title="написал ли клиент в WhatsApp после шаблона и до следующего шаблона ему же">Ответ</th>
                    <th className={th} title="что получатель сделал после отправки">Что сделал</th>
                    <th className={th} title="страница клиента из кнопки шаблона" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((x, i) => (
                    <ItemRow key={`${x.at}-${x.phone}-${x.kind}-${i}`} x={x} title={titles[x.kind] ?? x.kind} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </section>
  );
}
